<template>
    <div class="dashboard__body">
        <PreLoader v-if="isLoading"/>
        <div class="header-title text-center">
            <h3>{{ $t('analytics') }}</h3>
        </div>

        <div class="select-track-data" style="align-items: flex-start; gap: 8px">
            <div class="date-picker">
                <v-menu v-model="showPickerStart" :close-on-content-click="false" :nudge-right="40"
                        transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field outlined dense v-model="trackObj.startDate" :label="$t('from')" append-icon="mdi-calendar"
                                      readonly v-bind="attrs" v-on="on"/>
                    </template>
                    <v-date-picker :max="dateEnd" locale="ru-RU" v-model="dateStart"
                                   @input="onSelectTrackDate('showPickerStart')"/>
                </v-menu>
            </div>
            <div class="date-picker">
                <v-menu v-model="showPickerEnd" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field outlined dense v-model="trackObj.endDate" :label="$t('to')" append-icon="mdi-calendar"
                                      readonly v-bind="attrs" v-on="on"/>
                    </template>
                    <v-date-picker :max="dateEnd" locale="ru-RU" v-model="dateEnd"
                                   @input="onSelectTrackDate('showPickerEnd')"/>
                </v-menu>
            </div>
            <v-select :items="roles" :item-text="getRoleName" item-value="id" :label="$t('role')"
                      :rules="required" v-model="trackObj.roleId" outlined dense/>
            <div class="school-select">
                <v-autocomplete :items="schools" item-text="nameRu" item-value="id" :label="$t('school')"
                                v-model="trackObj.schoolId" clearable hide-no-data :loading="schoolLoader" :rules="required"
                                :search-input.sync="searchSchool" @change="onSelectSchool" outlined dense/>
            </div>
            <v-btn color="primary" style="height: 40px" @click="getAnalytics">{{ $t('show') }}</v-btn>
        </div>
        <table class="analytics table bordered">
            <thead>
            <tr>
                <th>№</th>
                <th>{{ $t('school') }}</th>
                <th>{{ $t('totalAmount') }}</th>
                <th>{{ $t('entersSystem') }}</th>
                <th>%</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(data, i) in analyticsData" :key="i">
                <td>{{ i + 1 }}</td>
                <td>{{ data.schoolName }}</td>
                <td>{{ data[getSpecificRoleCount()] }}</td>
                <td>{{ data.active.length }}</td>
                <td>{{ data.inPercent }}%</td>
            </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import AnalyticsService from '@/_services/analytics.service'
import moment from 'moment'
import { RoleService } from '@/_services/role.service'
import { SchoolService } from '@/_services/school.service'
import PreLoader from '@/components/preloader/PreLoader'
const roleService = new RoleService()
const schoolService = new SchoolService()

export default {
  name: 'SuperAdminDashboard',
  components: {
    PreLoader
  },

  data () {
    return {
      required: [v => !!v || this.$t('required')],
      trackObj: {
        startDate: '',
        endDate: moment().format('DD.MM.YYYY'),
        roleId: '',
        schoolId: ''
      },
      showPickerStart: false,
      showPickerEnd: false,
      isLoading: false,
      typingTimer: null,
      isTimerBlock: false,
      schoolLoader: false,
      dateStart: '',
      dateEnd: moment().format('YYYY-MM-DD'),
      currentRole: '',
      searchSchool: '',
      roles: [],
      schools: [],
      analyticsData: []
    }
  },

  async created () {
    this.setPreviousMonth()
    await this.fetchAllRoles()
    await this.fetchAllSchools()
  },

  methods: {
    setPreviousMonth () {
      const dateMonthAgo = new Date().setMonth(new Date().getMonth() - 1)
      this.trackObj.startDate = moment(dateMonthAgo).format('DD.MM.YYYY')
      this.dateStart = moment(dateMonthAgo).format('YYYY-MM-DD')
    },

    fetchAllRoles () {
      const activeRoles = ['ROLE_INSTRUCTOR', 'ROLE_STUDENT', 'ROLE_ADMIN']
      roleService.listPageable().then((res) => {
        res.forEach((roleObj) => {
          if (activeRoles.includes(roleObj.code)) {
            this.roles.push(roleObj)
          }
        })
      }).catch((err) => {
        this.$toast.error(err)
        this.isLoading = false
      })
    },

    fetchAllSchools (searchQuery = '') {
      schoolService.listPageable(0, '', '', searchQuery).then((res) => {
        if (res['_embedded']) {
          this.schools = res['_embedded']['schoolResourceList']
        }
        this.isLoading = false
        this.schoolLoader = false
      }).catch((err) => {
        this.$toast.error(err)
        this.isLoading = false
      })
    },

    onSelectTrackDate (picker) {
      this.trackObj.startDate = moment(this.dateStart, 'YYYY-MM-DD').format('DD.MM.YYYY')
      this.trackObj.endDate = moment(this.dateEnd, 'YYYY-MM-DD').format('DD.MM.YYYY')
      this[picker] = false
    },

    getRoleName (roleObj) {
      if (roleObj.code) {
        return this.$t(`${roleObj.code}`)
      }
    },

    getSpecificRoleCount () {
      const rolesObj = {
        ROLE_INSTRUCTOR: 'totalInstructors',
        ROLE_STUDENT: 'totalStudents',
        ROLE_ADMIN: 'totalAdmins'
      }
      return rolesObj[this.currentRole]
    },

    countPercent (uniqueUsers, allUsers) {
      if (allUsers) {
        const result = (uniqueUsers / allUsers) * 100
        return result.toFixed(1)
      }
      return 0
    },

    getAnalytics () {
      if (this.dateEnd < this.dateStart) {
        this.$toast.info(this.$t('endDateControl'))
        return
      }
      if (!this.trackObj.roleId) {
        this.$toast.info(this.$t('chooseRole'))
        return
      }
      if (!this.trackObj.schoolId) {
        this.$toast.info('Выберите школу')
        return
      }
      this.isLoading = true
      this.analyticsData = []
      this.currentRole = this.roles.find((role) => role.id === this.trackObj.roleId).code

      AnalyticsService.userTrackPage(
                    `${this.trackObj.startDate} 24:00`,
                    `${this.trackObj.endDate} 24:00`,
                    this.trackObj.roleId,
                    this.trackObj.schoolId
      ).then((res) => {
        this.analyticsData = res.map((school) => {
          school.totalAdmins = 1
          school.totalStudents = !school.totalStudents ? 0 : school.totalStudents
          school.active = !school.active ? [] : school.active
          school.inPercent = this.countPercent(school.active.length, school[this.getSpecificRoleCount()])
          return school
        }).sort((a, b) => b.inPercent - a.inPercent)
        this.isLoading = false
      }).catch((err) => {
        this.$toast.error(err)
        this.isLoading = false
      })
    },

    onSelectSchool () {
      this.isTimerBlock = true
      setTimeout(() => {
        this.isTimerBlock = false
      }, 3000)
    }
  },

  watch: {
    searchSchool (inputValue) {
      clearTimeout(this.typingTimer)
      this.typingTimer = null
      if (!inputValue) {
        this.schools = []
        return
      }
      if (!this.isTimerBlock) {
        this.schoolLoader = true
        this.typingTimer = setTimeout(() => {
          this.fetchAllSchools(inputValue)
        }, 900)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
tr, th, td {
    vertical-align: middle;
}
    .dashboard__body {
        margin: 30px;
        .select-track-data {
            display: flex;
            align-items: center;
            justify-content: center;
            .date-picker {
                max-width: 180px;
                margin-right: 15px;
            }
            .v-select {
                max-width: 210px;
                margin-right: 15px;
            }
            .school-select {
                display: flex;
                align-items: center;
                margin-right: 20px;
                .v-select {
                    margin-right: 5px;
                    max-width: 200px;
                }
            }
        }

        .role-title {
            text-align: center;
            h3 {
                color: #1890f0;
            }
        }

        table.analytics {
            max-width: 1024px;
            margin: 30px auto 0;
            th {
                white-space: nowrap;
            }
            th, td {
                font-weight: bold;
            }
        }
    }
</style>
